import * as React from "react"
import { Calendar, ExternalLink, ImageIcon } from "lucide-react"
import { TabsWidget, TabsWidgetContent, TabsWidgetList, TabsWidgetTrigger } from "./tabs-widget"
import { CardWidget, CardWidgetContent, CardWidgetDescription, CardWidgetHeader, CardWidgetTitle } from "./card-widget"
import { cn } from "../../lib/utils"

export interface ContentEntry {
  // Core fields
  title: string
  description: string
  date: string
  link: string
  linkText?: string
  
  // Event-specific fields
  speaker?: string
  time?: string
  location?: string
  price?: string
  duration?: string
  category?: string
  organizer?: string
  capacity?: string
  status?: 'available' | 'sold-out' | 'cancelled'
  
  // Optional content
  image?: string
  details?: string
  extras?: string
}

interface TabSection {
  id: string
  label: string
  content: React.ReactNode
}

interface TabbedWidgetProps {
  entry: ContentEntry
  customTabs?: TabSection[]
  className?: string
  showImage?: boolean
  defaultTab?: string
}

const TabbedWidget = React.forwardRef<
  React.ElementRef<typeof TabsWidget>,
  TabbedWidgetProps
>(({ entry, customTabs, className, showImage = true, defaultTab = "overview", ...props }, ref) => {
  const defaultTabs: TabSection[] = [
    {
      id: "overview",
      label: "Overview",
      content: (
        <CardWidget>
          <CardWidgetHeader>
            <CardWidgetTitle className="dsw-flex dsw-items-center dsw-gap-2">
              {entry.title}
              <span className="dsw-flex dsw-items-center dsw-gap-1 dsw-text-xs dsw-text-muted-foreground dsw-font-normal">
                <Calendar className="dsw-h-3 dsw-w-3" />
                {entry.date}
                {` • ${entry.time || 'Time TBA'}`}
              </span>
            </CardWidgetTitle>
            <CardWidgetDescription>{entry.description}</CardWidgetDescription>
          </CardWidgetHeader>
          <CardWidgetContent>
            {/* Event Details Grid */}
            <div className="dsw-grid dsw-grid-cols-1 md:dsw-grid-cols-2 dsw-gap-3 dsw-mb-4">
              <div>
                <strong className="dsw-text-sm">Speaker:</strong>
                <p className="dsw-text-sm dsw-text-muted-foreground">{entry.speaker || 'Speaker TBA'}</p>
              </div>
              {entry.location && (
                <div>
                  <strong className="dsw-text-sm">Location:</strong>
                  <p className="dsw-text-sm dsw-text-muted-foreground">{entry.location}</p>
                </div>
              )}
              {entry.price && (
                <div>
                  <strong className="dsw-text-sm">Price:</strong>
                  <p className="dsw-text-sm dsw-font-medium dsw-text-green-600">{entry.price}</p>
                </div>
              )}
              {entry.duration && (
                <div>
                  <strong className="dsw-text-sm">Duration:</strong>
                  <p className="dsw-text-sm dsw-text-muted-foreground">{entry.duration}</p>
                </div>
              )}
              {entry.organizer && (
                <div>
                  <strong className="dsw-text-sm">Organizer:</strong>
                  <p className="dsw-text-sm dsw-text-muted-foreground">{entry.organizer}</p>
                </div>
              )}
              {entry.capacity && (
                <div>
                  <strong className="dsw-text-sm">Capacity:</strong>
                  <p className="dsw-text-sm dsw-text-muted-foreground">{entry.capacity}</p>
                </div>
              )}
            </div>
            
            {entry.status && entry.status !== 'available' && (
              <div className="dsw-mb-4">
                <span className={cn(
                  "dsw-inline-block dsw-px-3 dsw-py-1 dsw-rounded dsw-text-sm dsw-font-medium",
                  entry.status === 'sold-out' && "dsw-bg-red-100 dsw-text-red-800",
                  entry.status === 'cancelled' && "dsw-bg-gray-100 dsw-text-gray-800"
                )}>
                  {entry.status === 'sold-out' ? 'Sold Out' : 'Cancelled'}
                </span>
              </div>
            )}
            
            {showImage && (
              <div className="dsw-aspect-video dsw-bg-muted dsw-flex dsw-items-center dsw-justify-center dsw-rounded-lg dsw-mb-4">
                {entry.image ? (
                  <img 
                    src={entry.image} 
                    alt={entry.title}
                    className="dsw-w-full dsw-h-full dsw-object-cover dsw-rounded-lg"
                  />
                ) : (
                  <ImageIcon className="dsw-h-16 dsw-w-16 dsw-text-muted-foreground" />
                )}
              </div>
            )}
            
            <a 
              href={entry.link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="dsw-inline-flex dsw-items-center dsw-justify-center dsw-whitespace-nowrap dsw-text-sm dsw-font-medium dsw-ring-offset-background dsw-transition-colors dsw-focus-visible:dsw-outline-none dsw-focus-visible:dsw-ring-2 dsw-focus-visible:dsw-ring-ring dsw-focus-visible:dsw-ring-offset-2 dsw-disabled:dsw-pointer-events-none dsw-disabled:dsw-opacity-50 dsw-cursor-pointer dsw-bg-white dsw-text-black dsw-border dsw-border-gray-200 hover:dsw-bg-black hover:dsw-text-white dsw-h-10 dsw-px-4 dsw-py-2 dsw-rounded-lg"
            >
              <ExternalLink className="dsw-h-4 dsw-w-4 dsw-mr-2" />
              {entry.linkText || "Open Link"}
            </a>
          </CardWidgetContent>
        </CardWidget>
      )
    },
    {
      id: "details",
      label: "Details",
      content: (
        <CardWidget>
          <CardWidgetHeader>
            <CardWidgetTitle>Additional Details</CardWidgetTitle>
          </CardWidgetHeader>
          <CardWidgetContent>
            <p className="dsw-text-sm dsw-text-muted-foreground">
              {entry.details || "No additional details available."}
            </p>
          </CardWidgetContent>
        </CardWidget>
      )
    },
    {
      id: "extras",
      label: "Extras",
      content: (
        <CardWidget>
          <CardWidgetHeader>
            <CardWidgetTitle>Extra Information</CardWidgetTitle>
          </CardWidgetHeader>
          <CardWidgetContent>
            <p className="dsw-text-sm dsw-text-muted-foreground">
              {entry.extras || "No extra information available."}
            </p>
          </CardWidgetContent>
        </CardWidget>
      )
    }
  ]

  const tabs = customTabs || defaultTabs

  return (
    <TabsWidget ref={ref} defaultValue={defaultTab} className={cn("dsw-w-full", className)} {...props}>
      <TabsWidgetList className="dsw-grid dsw-w-full dsw-grid-cols-3">
        {tabs.map((tab) => (
          <TabsWidgetTrigger key={tab.id} value={tab.id}>
            {tab.label}
          </TabsWidgetTrigger>
        ))}
      </TabsWidgetList>
      {tabs.map((tab) => (
        <TabsWidgetContent key={tab.id} value={tab.id}>
          {tab.content}
        </TabsWidgetContent>
      ))}
    </TabsWidget>
  )
})
TabbedWidget.displayName = "TabbedWidget"

export { TabbedWidget } 